import { definePermissions } from '../define-permissions';

/**
 * Permissions owned by the core. Plugins declare their own catalog with `definePermissions`
 * under their plugin id as `module`; the core never lists plugin permissions here.
 */
export const corePermissions = definePermissions({
  module: 'core',
  resources: {
    organization: {
      // Creating a tenant is a platform operation (`platform.organization.create`).
      actions: ['view', 'update', 'delete'],
      description: "The caller's own organization (tenant).",
    },
    unit: {
      actions: ['view', 'create', 'update'],
      description: 'Headquarters and branches of the organization, with business hours.',
    },
    holiday: {
      actions: ['view', 'create', 'update', 'delete'],
      description: 'Holidays of each unit; national holidays are read-only platform data.',
    },
    member: {
      actions: ['view', 'create', 'update', 'reset-password'],
      description: 'People of the organization: role, units, teams, data scope and status.',
    },
    team: {
      actions: ['view', 'create', 'update', 'delete'],
      description: 'Groups of members inside a unit.',
    },
    // Work areas. Declared ahead of their screens (only `view`, the navigation needs it) so
    // that the role matrix holds the day each screen ships; the other actions come with it.
    conversation: {
      // `view` reaches my conversations, the unassigned ones of my units and those I was
      // mentioned in; `view-all` adds the others within the data scope. `reply` writes to the
      // contact and private notes, `assign` sets the assignee, `resolve` changes the status,
      // `transfer` hands a conversation to a member, a team or a queue, `supervise`
      // follows the queues live and changes the availability of the attendants, and `start`
      // opens a conversation with a contact who has not written first (spec 015).
      actions: ['view', 'view-all', 'reply', 'assign', 'resolve', 'transfer', 'supervise', 'start'],
      description: 'Conversations of the inbox (channels, queues, assignment).',
    },
    queue: {
      // The data scope limits which: below `organization`, only the queues of one's units.
      actions: ['manage'],
      description: 'Queues of the inbox: connections, attendants, strategy and routing rules.',
    },
    receptionist: {
      // Organization-wide: a flow greets connections of any unit. Admin only by default.
      actions: ['manage'],
      description: 'Receptionist: the pre-attendance menu that sends new conversations to queues.',
    },
    'quick-reply': {
      // Personal quick replies only need `core.conversation.reply`.
      actions: ['manage-shared'],
      description: 'Quick replies shared with the whole organization.',
    },
    macro: {
      // Spec 050. Personal macros only need `core.conversation.reply`.
      actions: ['manage-shared'],
      description:
        'Macros (a reply, tags, priority, transfer and resolve in one click) shared with the organization.',
    },
    channel: {
      // Reading the connections only needs `core.conversation.view` (filters, queues).
      actions: ['manage'],
      description: 'Channel connections (numbers and accounts), their templates and auto-replies.',
    },
    'whatsapp-group': {
      // Which WhatsApp groups of a QR code connection reach the CRM (spec 024); creating and
      // managing the groups the number administers (spec 025). Seeing and answering a group
      // only needs `core.conversation.*`.
      actions: ['monitor', 'manage'],
      description:
        'WhatsApp groups of the QR code connections: which ones come to the CRM, and managing them.',
    },
    sla: {
      actions: ['view', 'manage'],
      description: 'Service level policies: first response and resolution targets.',
    },
    consent: {
      // Reading a contact's consent goes with the conversation or the customer.
      actions: ['manage'],
      description: 'Opt-in and opt-out of contacts, and the opt-out keywords.',
    },
    deal: {
      // No delete: a deal is archived and restored. `move` changes its stage (won, lost).
      actions: ['view', 'create', 'update', 'move', 'archive'],
      description: 'Deals of the pipelines: customer, owner, value, items and stage.',
    },
    pipeline: {
      actions: ['manage'],
      description: 'Pipelines and their stages (required fields, checklist). Admin only.',
    },
    item: {
      actions: ['manage'],
      description: 'The catalog of interest items offered in deals. Admin only.',
    },
    proposal: {
      // Proposals of a deal (spec 030): `send` shares the link, `approve` releases a discount
      // above the sender's limit, `delete` cancels, `manage` is the settings. Admin only manages.
      actions: ['view', 'create', 'update', 'send', 'approve', 'delete', 'manage'],
      description: 'Proposals sent to customers from a deal: items, discount, link and acceptance.',
    },
    'lost-reason': {
      actions: ['manage'],
      description: 'Reasons a deal is lost. Admin only.',
    },
    case: {
      // The ticket (specs 046 and 054): opened by hand for a customer, with its protocol,
      // reason and outcome. `resolve` closes it, `cancel` drops one opened by mistake,
      // `comment` writes (and edits or removes one's own) internal comments (spec 055),
      // `moderate` removes anybody's comment.
      actions: ['view', 'create', 'update', 'resolve', 'cancel', 'comment', 'moderate'],
      description: 'Tickets: protocol, status, reason, outcome, comments and custom fields.',
    },
    'service-reason': {
      actions: ['manage'],
      description: 'The contact reasons a service case is classified by. Admin only.',
    },
    satisfaction: {
      // The satisfaction survey (spec 047): `view` reads the scores (whoever sees reports),
      // `manage` sets up the question per queue (admin only).
      actions: ['view', 'manage'],
      description: 'Satisfaction survey (CSAT): scores and comments, and how it is asked.',
    },
    customer: {
      // No delete: a customer is archived and restored. `export-personal-data` hands the
      // customer's data to its holder (LGPD): admin only.
      actions: ['view', 'create', 'update', 'archive', 'export-personal-data'],
      description: 'Customers (individuals or companies): owner, unit, tags, origin, address.',
    },
    contact: {
      // `import` brings a spreadsheet of contacts (an address book) in: not the bulk import of
      // customers and deals, so a member can import their own contacts.
      actions: ['view', 'create', 'update', 'delete', 'import'],
      description: 'People one talks to (of a customer or not yet), with their channels.',
    },
    'custom-field': {
      actions: ['manage'],
      description: 'Definitions of the custom fields of customers, contacts and deals. Admin only.',
    },
    task: {
      actions: ['view', 'create', 'update', 'delete'],
      description: 'Tasks, reminders and follow-ups on customers and deals.',
    },
    note: {
      // No view: a note is read with the record it belongs to (customer or deal).
      actions: ['create', 'update', 'delete'],
      description: 'Notes with mentions on customers and deals.',
    },
    'task-type': {
      actions: ['manage'],
      description: 'The configurable types of tasks (call, meeting…). Admin only.',
    },
    import: {
      actions: ['run'],
      description: 'Importing customers and deals from a spreadsheet, and the imports history.',
    },
    export: {
      actions: ['run'],
      description: 'Exporting the customers and deals lists (the data scope limits the rows).',
    },
    // Management areas: managers and admins only (MANAGEMENT_CORE_SUBJECTS).
    capture: {
      // The Captação area (spec 025): where the leads come from. The origin card on a record
      // is read with the record's own `view`.
      actions: ['view'],
      description: 'The lead capture area: where the leads come from, by channel and campaign.',
    },
    referral: {
      // The referral program (spec 028): `update` configures it and approves, delivers or
      // cancels the rewards. A customer's link is read with the customer's own `view`.
      actions: ['view', 'update'],
      description: 'The referral program: its settings, the referrals and the rewards.',
    },
    form: {
      // Forms answered at a public address (spec 026). `publish` puts a version on the air,
      // closes, reopens and changes the address.
      actions: ['view', 'create', 'update', 'delete', 'publish'],
      description: 'Forms of the capture area: questions, design, publishing and answers.',
    },
    campaign: {
      // `send` fires, pauses, cancels and approves a send (spec 022, rule 12).
      actions: ['view', 'create', 'update', 'delete', 'send'],
      description: 'Broadcasts, journeys, events, audiences and the sending rules.',
    },
    automation: {
      // Manager only views (MANAGER_VIEW_ONLY); admin creates and edits (F7).
      actions: ['view', 'create', 'update', 'delete'],
      description: 'Automation rules and cadences.',
    },
    'ai-agent': {
      // `manage` sets company instructions, kill switch and knowledge (admin only). The LLM
      // provider is platform-wide (`platform.ai`); who may use the assistant is an allow-list
      // of members set by the platform operator.
      actions: ['view', 'manage'],
      description: 'AI agents, their knowledge base and the organization AI preferences.',
    },
    integration: {
      // API keys, outgoing webhooks and lead sources (spec 013): what systems outside may do
      // with the organization's data. Admin only (`manage` is only in the admin preset).
      actions: ['manage'],
      description: 'API keys, outgoing webhooks and lead sources of the organization.',
    },
    report: { actions: ['view'], description: 'Reports and dashboards.' },
    audit: {
      actions: ['view'],
      description: 'Audit log: who did what in the organization. Admin only.',
    },
    plan: {
      // `view` shows the plan, its limits and the usage; `request` asks the platform for
      // another plan (the platform operator decides). Admin only: it is the contract.
      actions: ['view', 'request'],
      description: "The organization's plan: limits, usage and plan change requests.",
    },
  },
});

export type CorePermissionId = (typeof corePermissions.ids)[number];

/**
 * Core subjects that only `admin` changes: the `manager` preset gets just `view` on them
 * (organization settings, units and their holidays shape the whole tenant).
 */
export const ADMIN_ONLY_CORE_SUBJECTS = ['core.organization', 'core.unit', 'core.holiday'] as const;

/**
 * Management areas: the `member` preset gets nothing on them (a member does not create
 * campaigns by default — grantable to a membership later). Managers and admins do.
 */
export const MANAGEMENT_CORE_SUBJECTS = [
  'core.capture',
  'core.referral',
  'core.form',
  'core.campaign',
  'core.automation',
  'core.ai-agent',
  'core.report',
  'core.satisfaction',
] as const;

/** Core subjects only the `admin` preset gets, not even `view` for the others. */
export const ADMIN_EXCLUSIVE_CORE_SUBJECTS = ['core.audit', 'core.plan'] as const;

/**
 * Core subjects the `member` preset gets nothing of: the service level policies are read by
 * whoever supervises (managers) and changed by the admin; an attendant sees the deadlines on
 * the conversations, not the policies.
 */
export const SUPERVISION_CORE_SUBJECTS = ['core.sla'] as const;

/**
 * Permissions the `manager` preset gets beyond the standard CRUD: working every conversation
 * of the data scope, transferring and supervising them, managing the queues of their units,
 * changing a contact's consent (opt-in/opt-out) and its keywords, sharing quick replies, archiving customers and deals, moving deals,
 * importing, exporting and firing campaigns are custom actions (only `admin` would get them otherwise).
 * Exporting a customer's personal data stays with the admin.
 */
export const MANAGER_EXTRA_CORE_PERMISSIONS = [
  'core.conversation.view-all',
  'core.conversation.reply',
  'core.conversation.assign',
  'core.conversation.resolve',
  'core.conversation.transfer',
  'core.conversation.supervise',
  'core.conversation.start',
  'core.quick-reply.manage-shared',
  'core.macro.manage-shared',
  'core.queue.manage',
  'core.whatsapp-group.monitor',
  'core.whatsapp-group.manage',
  'core.consent.manage',
  'core.customer.archive',
  'core.case.resolve',
  'core.case.cancel',
  'core.case.comment',
  'core.case.moderate',
  'core.deal.move',
  'core.deal.archive',
  'core.import.run',
  'core.contact.import',
  'core.export.run',
  'core.campaign.send',
  'core.form.publish',
  'core.proposal.send',
  'core.proposal.approve',
] as const;

/**
 * Permissions the `member` preset gets beyond `view`: an attendant answers, takes, transfers and
 * resolves their conversations and the unassigned ones of their units (not the others': no
 * `view-all`), and starts conversations with contacts (the SDR's first message); a salesperson registers and keeps
 * their own customers, contacts and deals, moves their deals along the pipeline and keeps
 * their tasks and notes, and exports the lists they see (the data scope and the authorship
 * limit which ones). Archiving and importing stay with managers and admins.
 */
export const MEMBER_EXTRA_CORE_PERMISSIONS = [
  'core.conversation.reply',
  'core.conversation.assign',
  'core.conversation.resolve',
  'core.conversation.transfer',
  'core.conversation.start',
  'core.customer.create',
  'core.customer.update',
  'core.contact.create',
  'core.contact.update',
  'core.contact.delete',
  'core.contact.import',
  'core.case.create',
  'core.case.update',
  'core.case.resolve',
  'core.case.comment',
  'core.deal.create',
  'core.deal.update',
  'core.deal.move',
  'core.proposal.create',
  'core.proposal.update',
  'core.proposal.send',
  'core.task.create',
  'core.task.update',
  'core.task.delete',
  'core.note.create',
  'core.note.update',
  'core.note.delete',
  'core.export.run',
] as const;

/**
 * The sales subjects (spec 044): a member who works in `service` gets nothing of them, whatever
 * the role — a service team neither sells nor captures leads.
 */
export const SALES_CORE_SUBJECTS = [
  'core.deal',
  'core.pipeline',
  'core.item',
  'core.proposal',
  'core.lost-reason',
  'core.capture',
  'core.referral',
  'core.form',
] as const;

/**
 * Management subjects the `manager` preset only views: automations and AI agents are
 * configured by the admin for now, and so are the service level policies.
 */
export const MANAGER_VIEW_ONLY_CORE_SUBJECTS = [
  ...ADMIN_ONLY_CORE_SUBJECTS,
  'core.automation',
  'core.ai-agent',
  'core.sla',
] as const;
